/**
 * V2PaywallHeaderSection — V2-native editor section for paywall header
 *
 * Edits: Close Button (X icon) + Restore Button
 * V2 paths: content.paywall.closeButton, content.paywall.restoreLabel, content.paywall.headerAppearance
 *
 * Replaces: OnboardingPaywallHeaderSection (for V2 context)
 * Reuses: StyleFieldRow, SAccordion, EditorGuideProvider
 *
 * Key V2 differences from V1:
 * - Close alignment: 'start'/'end' (was 'left'/'right')
 * - Close visibility: 'delayedVisible' (was 'visibleAfterTimer')
 * - Restore text: translations.{locale} (was l10n.{locale})
 * - Restore styles: appearance.* (was styles.*)
 *
 * Sprint: schema-v2-migration, Sprint 4
 */

import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { Box, Typography, Checkbox, Collapse, IconButton, Slider } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { SAccordion, StyledTextField } from '@modules/ui-components';
import { EditorGuideProvider } from '../../../contexts';
import {
  V2_PAYWALL_CLOSE_BUTTON_CONFIG,
  V2_PAYWALL_CLOSE_ICON_COLOR_CONFIG,
  V2_PAYWALL_CLOSE_ALIGNMENT_CONFIG,
  V2_PAYWALL_CLOSE_APPEARANCE_CONFIG,
  V2_PAYWALL_CLOSE_TIMER_CONFIG,
  V2_PAYWALL_RESTORE_BUTTON_CONFIG,
  V2_PAYWALL_RESTORE_STYLE_FIELDS,
  createDefaultV2CloseButton,
  createDefaultV2RestoreLabel,
  createDefaultV2HeaderAppearance,
} from '../../../config/v2PaywallHeaderConfig';
import { withScreenEditorFieldGuide, withScreenEditorFieldGuides } from '../../../config/screenEditorFieldGuideResolver';
import { SECTION_MARGIN_BOTTOM, FIELD_GAP, FIELD_MARGIN_BOTTOM } from '../../../config/ui';
import {
  expandedToggleButtonSx,
  expandedToggleButtonActiveSx,
  expandedSectionSx,
  checkboxSx,
  checkboxIconSx,
  checkboxCheckedIconSx,
} from '../../../config/editorFieldStyles';
import { getValueByPath, isFieldEnabled } from '../../../utils/v2FieldOperations';
import { StyleFieldRow } from '../../shared/styleFields';
import { getSectionTestId } from '../../../utils/testIdUtils';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';

export interface V2PaywallHeaderSectionProps {
  step: FlowStepNode;
  locale: string;
  onChange: (path: string, value: unknown) => void;
  disabled?: boolean;
  defaultExpanded?: boolean;
  /** Override accordion title (used by FlexibleLayout for section labels) */
  sectionTitle?: string;
}

export const V2PaywallHeaderSection: React.FC<V2PaywallHeaderSectionProps> = ({
  step,
  locale,
  onChange,
  disabled = false,
  defaultExpanded = false,
  sectionTitle,
}) => {
  const [closeStylesExpanded, setCloseStylesExpanded] = useState(false);
  const [restoreStylesExpanded, setRestoreStylesExpanded] = useState(false);
  const [localRestoreText, setLocalRestoreText] = useState('');

  // ── Close Button state ──
  const closeButtonEnabled = useMemo(
    () => isFieldEnabled(step, V2_PAYWALL_CLOSE_BUTTON_CONFIG.path),
    [step]
  );

  const iconColor = useMemo(
    () => getValueByPath<string>(step, V2_PAYWALL_CLOSE_ICON_COLOR_CONFIG.path) ?? '#ffffff',
    [step]
  );

  const closeAlignment = useMemo(
    () => getValueByPath<string>(step, V2_PAYWALL_CLOSE_ALIGNMENT_CONFIG.path) ?? 'start',
    [step]
  );

  const closeAppearance = useMemo(
    () => getValueByPath<string>(step, V2_PAYWALL_CLOSE_APPEARANCE_CONFIG.path) ?? 'visible',
    [step]
  );

  const closeTimerValue = useMemo(
    () => getValueByPath<number>(step, V2_PAYWALL_CLOSE_TIMER_CONFIG.path) ?? 5,
    [step]
  );

  // ── Restore Button state ──
  const restoreButtonEnabled = useMemo(
    () => isFieldEnabled(step, V2_PAYWALL_RESTORE_BUTTON_CONFIG.path),
    [step]
  );

  const restoreText = useMemo(() => {
    const path = `content.paywall.restoreLabel.translations.${locale}`;
    return getValueByPath<string>(step, path) ?? '';
  }, [step, locale]);

  useEffect(() => {
    setLocalRestoreText(restoreText);
  }, [restoreText]);

  // ── Close Button handlers ──
  const handleCloseToggleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const enabled = event.target.checked;
      if (enabled) {
        onChange(V2_PAYWALL_CLOSE_BUTTON_CONFIG.path, createDefaultV2CloseButton());
        const existingAppearance = getValueByPath(step, 'content.paywall.headerAppearance');
        if (!existingAppearance) {
          onChange('content.paywall.headerAppearance', createDefaultV2HeaderAppearance());
        }
      } else {
        onChange(V2_PAYWALL_CLOSE_BUTTON_CONFIG.path, null);
      }
    },
    [onChange, step]
  );

  const handleCloseStylesToggle = useCallback(() => {
    setCloseStylesExpanded((prev) => !prev);
  }, []);

  const handleIconColorChange = useCallback(
    (newValue: unknown) => onChange(V2_PAYWALL_CLOSE_ICON_COLOR_CONFIG.path, newValue),
    [onChange]
  );

  const handleCloseAlignmentChange = useCallback(
    (newValue: unknown) => onChange(V2_PAYWALL_CLOSE_ALIGNMENT_CONFIG.path, newValue),
    [onChange]
  );

  const handleCloseAppearanceChange = useCallback(
    (newValue: unknown) => onChange(V2_PAYWALL_CLOSE_APPEARANCE_CONFIG.path, newValue),
    [onChange]
  );

  const handleCloseTimerChange = useCallback(
    (_event: Event, newValue: number | number[]) => {
      onChange(V2_PAYWALL_CLOSE_TIMER_CONFIG.path, newValue as number);
    },
    [onChange]
  );

  // ── Restore Button handlers ──
  const handleRestoreToggleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const enabled = event.target.checked;
      if (enabled) {
        onChange(V2_PAYWALL_RESTORE_BUTTON_CONFIG.path, createDefaultV2RestoreLabel(locale));
      } else {
        onChange(V2_PAYWALL_RESTORE_BUTTON_CONFIG.path, null);
      }
    },
    [onChange, locale]
  );

  const handleRestoreStylesToggle = useCallback(() => {
    setRestoreStylesExpanded((prev) => !prev);
  }, []);

  const handleRestoreTextBlur = useCallback(() => {
    if (localRestoreText !== restoreText) {
      onChange(`content.paywall.restoreLabel.translations.${locale}`, localRestoreText);
    }
  }, [localRestoreText, restoreText, locale, onChange]);

  const handleRestoreStyleChange = useCallback(
    (path: string) => (newValue: unknown) => {
      onChange(path, newValue);
    },
    [onChange]
  );

  return (
    <SAccordion
      title={sectionTitle ?? 'Header'}
      collapsible
      defaultExpanded={defaultExpanded}
      data-testid={getSectionTestId('paywall-header')}
      sx={{ mb: SECTION_MARGIN_BOTTOM }}
    >
      <EditorGuideProvider>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Close Button Section */}
          <Box data-testid="field-close-button">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: closeButtonEnabled && closeStylesExpanded ? FIELD_MARGIN_BOTTOM : 0,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: FIELD_GAP }}>
                <Checkbox
                  data-testid="paywall-header-close-enable"
                  checked={closeButtonEnabled}
                  onChange={handleCloseToggleChange}
                  disabled={disabled}
                  size="small"
                  sx={checkboxSx}
                  icon={<Box sx={checkboxIconSx} />}
                  checkedIcon={<Box sx={checkboxCheckedIconSx}>✓</Box>}
                />
                <Typography
                  variant="body2"
                  color={closeButtonEnabled ? 'text.primary' : 'text.secondary'}
                >
                  Close Button (X)
                </Typography>
              </Box>
              {closeButtonEnabled && (
                <IconButton
                  data-testid="paywall-header-close-styles-toggle"
                  size="small"
                  onClick={handleCloseStylesToggle}
                  disabled={disabled}
                  sx={closeStylesExpanded ? expandedToggleButtonActiveSx : expandedToggleButtonSx}
                >
                  <TuneIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            {closeButtonEnabled && (
              <Collapse in={closeStylesExpanded} timeout="auto" unmountOnExit>
                <Box sx={expandedSectionSx}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    <StyleFieldRow
                      config={withScreenEditorFieldGuide(V2_PAYWALL_CLOSE_ICON_COLOR_CONFIG, 'paywallHeader')}
                      value={iconColor}
                      onChange={handleIconColorChange}
                      disabled={disabled}
                    />
                    <StyleFieldRow
                      config={withScreenEditorFieldGuide(V2_PAYWALL_CLOSE_ALIGNMENT_CONFIG, 'paywallHeader')}
                      value={closeAlignment}
                      onChange={handleCloseAlignmentChange}
                      disabled={disabled}
                    />
                    <StyleFieldRow
                      config={withScreenEditorFieldGuide(V2_PAYWALL_CLOSE_APPEARANCE_CONFIG, 'paywallHeader')}
                      value={closeAppearance}
                      onChange={handleCloseAppearanceChange}
                      disabled={disabled}
                    />
                    {closeAppearance === 'delayedVisible' && (
                      <Box data-testid="paywall-header-close-delay" sx={{ gridColumn: '1 / -1', px: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">
                            Visible after
                          </Typography>
                          <Typography variant="caption" fontWeight="medium">
                            {closeTimerValue} sec
                          </Typography>
                        </Box>
                        <Slider
                          value={closeTimerValue}
                          onChange={handleCloseTimerChange}
                          min={V2_PAYWALL_CLOSE_TIMER_CONFIG.min}
                          max={V2_PAYWALL_CLOSE_TIMER_CONFIG.max}
                          step={V2_PAYWALL_CLOSE_TIMER_CONFIG.step}
                          disabled={disabled}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              </Collapse>
            )}
          </Box>

          {/* Restore Button Section */}
          <Box data-testid="field-restore-button">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: restoreButtonEnabled ? FIELD_MARGIN_BOTTOM : 0,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: FIELD_GAP }}>
                <Checkbox
                  data-testid="paywall-header-restore-enable"
                  checked={restoreButtonEnabled}
                  onChange={handleRestoreToggleChange}
                  disabled={disabled}
                  size="small"
                  sx={checkboxSx}
                  icon={<Box sx={checkboxIconSx} />}
                  checkedIcon={<Box sx={checkboxCheckedIconSx}>✓</Box>}
                />
                <Typography
                  variant="body2"
                  color={restoreButtonEnabled ? 'text.primary' : 'text.secondary'}
                >
                  Restore Button
                </Typography>
              </Box>
              {restoreButtonEnabled && (
                <IconButton
                  data-testid="paywall-header-restore-styles-toggle"
                  size="small"
                  onClick={handleRestoreStylesToggle}
                  disabled={disabled}
                  sx={restoreStylesExpanded ? expandedToggleButtonActiveSx : expandedToggleButtonSx}
                >
                  <TuneIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            {restoreButtonEnabled && (
              <Box data-testid="paywall-header-restore-text" sx={{ mb: FIELD_MARGIN_BOTTOM }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Text
                </Typography>
                <StyledTextField
                  value={localRestoreText}
                  onChange={setLocalRestoreText}
                  onBlur={handleRestoreTextBlur}
                  placeholder="Restore Purchases"
                  disabled={disabled}
                />
              </Box>
            )}

            {restoreButtonEnabled && (
              <Collapse in={restoreStylesExpanded} timeout="auto" unmountOnExit>
                <Box sx={expandedSectionSx}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    {withScreenEditorFieldGuides(V2_PAYWALL_RESTORE_STYLE_FIELDS, 'paywallHeader').map((config) => (
                      <StyleFieldRow
                        key={config.path}
                        config={config}
                        value={getValueByPath(step, config.path) ?? config.defaultValue}
                        onChange={handleRestoreStyleChange(config.path)}
                        disabled={disabled}
                      />
                    ))}
                  </Box>
                </Box>
              </Collapse>
            )}
          </Box>
        </Box>
      </EditorGuideProvider>
    </SAccordion>
  );
};

export default V2PaywallHeaderSection;
